/**
 * Word Matching Game
 * Connect pictures with words
 */
import { useState, useEffect } from 'react';
import { VocabularyWord } from '../../data/vocabulary';

interface WordMatchingGameProps {
  words: VocabularyWord[];
  onComplete: (score: number) => void;
  onExit: () => void;
}

export default function WordMatchingGame({ words, onComplete, onExit }: WordMatchingGameProps) {
  const [cards, setCards] = useState<Array<{id: string; type: 'image' | 'word'; content: string; matched: boolean; selected: boolean; wordId: string}>>([]);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [matches, setMatches] = useState(0);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  // Initialize game with random words
  useEffect(() => {
    const gameWords = words.slice(0, 6); // Use 6 words for 12 cards
    const imageCards = gameWords.map(w => ({
      id: `img-${w.id}`,
      type: 'image' as 'image',
      content: w.image || '📚',
      matched: false,
      selected: false,
      wordId: w.id
    }));
    const wordCards = gameWords.map(w => ({
      id: `word-${w.id}`,
      type: 'word' as 'word',
      content: w.word,
      matched: false,
      selected: false,
      wordId: w.id
    }));

    // Shuffle cards
    const allCards = [...imageCards, ...wordCards].sort(() => Math.random() - 0.5);
    setCards(allCards);
  }, [words]);

  const handleCardClick = (cardId: string) => {
    if (selectedCards.length >= 2) return;

    const card = cards.find(c => c.id === cardId);
    if (!card || card.matched || card.selected) return;

    // Update card selection
    setCards(prev => prev.map(c =>
      c.id === cardId ? { ...c, selected: true } : c
    ));

    const newSelected = [...selectedCards, cardId];
    setSelectedCards(newSelected);

    if (newSelected.length === 2) {
      // Check for match
      const [first, second] = newSelected;
      const card1 = cards.find(c => c.id === first);
      const card2 = cards.find(c => c.id === second);

      if (card1 && card2 && card1.wordId === card2.wordId && card1.type !== card2.type) {
        // Match found!
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.id === first || c.id === second ? { ...c, matched: true, selected: false } : c
          ));
          setMatches(m => m + 1);
          setScore(s => s + 15);
          setSelectedCards([]);
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 1000);
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.id === first || c.id === second ? { ...c, selected: false } : c
          ));
          setSelectedCards([]);
        }, 1000);
      }

      setAttempts(a => a + 1);
    }
  };

  // Check game completion
  useEffect(() => {
    if (matches === cards.length / 2 && cards.length > 0) {
      const finalScore = score - (attempts - matches) * 2;
      setTimeout(() => {
        onComplete(Math.max(0, finalScore));
      }, 1500);
    }
  }, [matches, cards.length]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-700">
          ← 返回
        </button>
        <div className="text-center">
          <h2 className="text-xl font-bold text-purple-600">🧩 单词连连看</h2>
          <p className="text-sm text-gray-500">找到配对的图片和单词</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-purple-600">得分: {score}</p>
          <p className="text-xs text-gray-400">配对: {matches}/{cards.length/2}</p>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-4 gap-3 max-w-2xl mx-auto">
        {cards.map(card => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            disabled={card.matched}
            className={`
              h-24 rounded-xl flex items-center justify-center text-2xl
              transition-all duration-300 transform
              ${card.matched
                ? 'bg-green-100 text-green-600 scale-95 opacity-75'
                : card.selected
                ? 'bg-purple-100 border-2 border-purple-400 scale-105'
                : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:scale-105'
              }
              shadow-md
            `}
          >
            {card.type === 'image' ? (
              <span className="text-4xl">{card.content}</span>
            ) : (
              <span className="text-lg font-bold text-gray-700">{card.content}</span>
            )}
          </button>
        ))}
      </div>

      {/* Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-6xl animate-bounce-in">🎉</div>
        </div>
      )}

      {/* Completion screen */}
      {matches === cards.length / 2 && cards.length > 0 && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center animate-bounce-in">
          <div className="text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold text-purple-600 mb-2">太棒了！</h2>
            <p className="text-lg text-gray-600">得分: {score} 分</p>
            <p className="text-sm text-gray-400">尝试 {attempts} 次，配对 {matches} 组</p>
            <button
              onClick={onExit}
              className="mt-6 px-6 py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600"
            >
              完成 ✓
            </button>
          </div>
        </div>
      )}
    </div>
  );
}